// Accept an invitation (Lane 1331). The link carries its token in the URL
// fragment (#t=...), which browsers never send to a server. The page reads it,
// removes it from the address bar at once, and posts it.
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicShell } from './PortalShell';
import { portalApi, setPortalToken, PASSWORD_MIN, PASSWORD_MAX, PORTAL_LOGIN } from './portalApi';

function readHashToken() {
  try {
    const hash = window.location.hash || '';
    return new URLSearchParams(hash.replace(/^#/, '')).get('t') || '';
  } catch (e) {
    return '';
  }
}

function InvalidLink() {
  return (
    <div className="srcp-card">
      <div className="srcp-card-h"><h1 className="srcp-card-t srcp-h2">This link is not valid</h1></div>
      <div className="srcp-card-b srcp-card-b--form">
        <div className="srcp-text">
          This link is not valid. It may be used, expired or withdrawn. Ask the buyer for a new one, or sign in.
        </div>
        <div><Link className="srcp-btn srcp-btn--primary" to={PORTAL_LOGIN}>Sign in</Link></div>
      </div>
    </div>
  );
}

export default function JoinPage() {
  const navigate = useNavigate();
  // Read in the initializer (pure); the address bar is cleaned in the effect.
  const [token] = useState(readHashToken);
  const started = useRef(false);
  const [invite, setInvite] = useState(null);
  const [phase, setPhase] = useState(token ? 'loading' : 'invalid');
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
    if (!token || started.current) return;
    started.current = true;
    portalApi.inspect(token)
      .then((r) => { setInvite(r); setPhase('form'); })
      .catch((e) => {
        if (e.status === 404 || e.status === 422) { setPhase('invalid'); return; }
        setLoadError(e.message);
        setPhase('error');
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
      setError(`Use a password of ${PASSWORD_MIN} to ${PASSWORD_MAX} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords are not the same.');
      return;
    }
    setSaving(true);
    try {
      const r = await portalApi.accept(token, password, name.trim() || null);
      setPortalToken(r.token);
      navigate(`/sourcing-portal/events/${encodeURIComponent(r.event_public_id)}`, { replace: true });
    } catch (err) {
      if (err.status === 404) { setPhase('invalid'); return; }
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PublicShell>
      {phase === 'loading' && <div className="srcp-loading">Checking your invitation…</div>}
      {phase === 'invalid' && <InvalidLink />}
      {phase === 'error' && <div className="srcp-error" role="alert">{loadError}</div>}
      {phase === 'form' && invite && (
        <form className="srcp-card" onSubmit={submit} noValidate>
          <div className="srcp-card-h">
            <h1 className="srcp-card-t srcp-h2">Join to quote for {invite.buyer_name}</h1>
          </div>
          <div className="srcp-card-b srcp-card-b--form">
            <div className="srcp-mut">{invite.event_title}</div>
            <div className="srcp-field">
              <label className="srcp-label" htmlFor="srcp-join-email">Email</label>
              <input id="srcp-join-email" className="srcp-input" value={invite.email || ''} readOnly />
            </div>
            <div className="srcp-field">
              <label className="srcp-label" htmlFor="srcp-join-name">Your name</label>
              <input id="srcp-join-name" className="srcp-input" value={name} maxLength={255} autoComplete="name"
                onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="srcp-field">
              <label className="srcp-label" htmlFor="srcp-join-pw">Password</label>
              <input id="srcp-join-pw" className="srcp-input" type="password" value={password} maxLength={PASSWORD_MAX}
                autoComplete="new-password" placeholder={`${PASSWORD_MIN} characters or more`}
                onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="srcp-field">
              <label className="srcp-label" htmlFor="srcp-join-pw2">Confirm password</label>
              <input id="srcp-join-pw2" className="srcp-input" type="password" value={confirm} maxLength={PASSWORD_MAX}
                autoComplete="new-password" onChange={(e) => setConfirm(e.target.value)} />
            </div>
            {error && <div className="srcp-error" role="alert">{error}</div>}
            <div>
              <button type="submit" className="srcp-btn srcp-btn--primary" disabled={saving || !password || !confirm}>
                {saving ? 'Joining…' : 'Join and open the event'}
              </button>
            </div>
            <div className="srcp-signin-note">Already have a Wingman supplier account for this email? Use its password.</div>
          </div>
        </form>
      )}
    </PublicShell>
  );
}
